import { useState } from "react";
import { Input } from "../ui/input";

const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

/** Searchable chip picker for the 70 trades: selected chips first, a filter box, then the catalogue. */
export function TradePicker({ all = [], value = [], onChange, max = 20 }) {
  const [q, setQ] = useState("");
  const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : value.length >= max ? value : [...value, t]);
  const shown = all.filter((t) => !q || t.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="listing-trades">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="listing-trades-selected">
          {value.map((t) => (
            <button key={t} type="button" onClick={() => toggle(t)} className="rounded-full border border-primary bg-primary text-primary-foreground px-3 py-1 text-xs" data-testid={`trade-selected-${slug(t)}`} title="Remove">{t} ×</button>
          ))}
        </div>
      )}
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to find a trade… e.g. fire, gas, roof" className="h-9" data-testid="trade-search-input" />
      <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-1">
        {shown.map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)} data-testid={`trade-chip-${slug(t)}`} disabled={!value.includes(t) && value.length >= max}
            className={`rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-40 ${value.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>{t}</button>
        ))}
        {shown.length === 0 && <p className="text-xs text-muted-foreground py-1">No trade matches “{q}” — add it under “Other trades” below.</p>}
      </div>
    </div>
  );
}

/** Counties / regions grouped by nation, with a filter box and per-nation collapse. */
export function RegionPicker({ groups = {}, value = [], onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => Object.fromEntries(Object.keys(groups).map((k) => [k, k === "England"])));
  const toggle = (r) => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  const ql = q.toLowerCase();
  return (
    <div className="space-y-2" data-testid="listing-regions">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a county or region…" className="h-9 max-w-xs" data-testid="region-search-input" />
        {value.length > 0 && <span className="text-xs text-muted-foreground" data-testid="listing-regions-count">{value.length} selected</span>}
        {value.length > 0 && <button type="button" onClick={() => onChange([])} className="text-xs text-muted-foreground underline" data-testid="regions-clear-btn">Clear</button>}
      </div>
      {Object.entries(groups).map(([nation, list]) => {
        const shown = list.filter((r) => !ql || r.toLowerCase().includes(ql));
        if (ql && shown.length === 0) return null;
        const picked = list.filter((r) => value.includes(r)).length;
        const isOpen = ql ? true : open[nation];
        return (
          <div key={nation} className="rounded-md border border-border">
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [nation]: !o[nation] }))} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium" data-testid={`region-group-${slug(nation)}`}>
              <span>{nation}{picked ? <span className="ml-2 text-xs text-primary">{picked} selected</span> : null}</span><span className="text-muted-foreground text-xs">{isOpen ? "Hide" : "Show"}</span>
            </button>
            {isOpen && (
              <div className="px-3 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-1">
                {shown.map((r) => (
                  <label key={r} className="inline-flex items-center gap-2 text-xs cursor-pointer py-0.5">
                    <input type="checkbox" checked={value.includes(r)} onChange={() => toggle(r)} data-testid={`region-check-${slug(r)}`} /> {r}
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
